import { Capacitor, registerPlugin } from "@capacitor/core";
import { v4 as uuidv4 } from "uuid";
import type { SpeechRun } from "./snag-speech-telemetry";
import {
  createSpeechRun,
  recordSpeechEvent,
  speechFailureDetails,
} from "./snag-speech-telemetry";

type SpeechModelStatus =
  | "app-update"
  | "missing"
  | "pending"
  | "ready"
  | "unavailable"
  | "unsupported-os";
interface SnagSpeechPlugin {
  getStatus(options: { language: string }): Promise<{
    status: SpeechModelStatus;
    reason?: string;
    errorCode?: number;
  }>;
  downloadModel(options: { language: string }): Promise<void>;
  transcribeFile(options: {
    language: string;
    uri: string;
  }): Promise<{ text: string }>;
}
const SnagSpeech = registerPlugin<SnagSpeechPlugin>("SnagSpeech");
// Native capability checks below decide whether dictation can be shown.
const canTranscribeSnagVocals = () => Capacitor.isNativePlatform();
const snagDictationLanguage = "fr-FR";
const lastCapabilities = new Map<string, string>();
const getSpeechModelStatus = async (
  language: string
): Promise<SpeechModelStatus> => {
  let result: {
    status: SpeechModelStatus;
    reason?: string;
    errorCode?: number;
  };
  if (!canTranscribeSnagVocals())
    result = { reason: "web", status: "unavailable" };
  else if (!Capacitor.isPluginAvailable("SnagSpeech"))
    result = { reason: "plugin-missing", status: "app-update" };
  else result = await SnagSpeech.getStatus({ language });
  const status = [
    "ready",
    "missing",
    "pending",
    "unsupported-os",
    "unavailable",
    "app-update",
  ].includes(result?.status)
    ? result.status
    : "unavailable";
  const reasons = [
    "web",
    "plugin-missing",
    "unsupported-os",
    "on-device-unavailable",
    "locale-unsupported",
    "support-check-failed",
    "model-installed",
    "model-pending",
    "model-missing",
    "compiler-unavailable",
  ];
  const reason =
    reasons.find((value) => value === result?.reason) ??
    (status !== result?.status ? "invalid-status" : "legacy-native-status");
  const nativeErrorCode =
    typeof result?.errorCode === "number" && Number.isFinite(result.errorCode)
      ? result.errorCode
      : undefined;
  const key = JSON.stringify([status, reason, nativeErrorCode]);
  if (lastCapabilities.get(language) !== key) {
    lastCapabilities.set(language, key);
    recordSpeechEvent(
      "capability-status",
      { language, nativeErrorCode, reason, status },
      reason !== "web" && (status === "unavailable" || status === "app-update")
    );
  }
  return status;
};
// An editor can close and reopen while iOS is still installing its model.
const modelDownloads = new Map<string, Promise<void>>();
const downloadSpeechModel = (language: string): Promise<void> => {
  const existing = modelDownloads.get(language);
  if (existing) return existing;
  const download = SnagSpeech.downloadModel({ language }).finally(() => {
    modelDownloads.delete(language);
  });
  modelDownloads.set(language, download);
  return download;
};

const encodePcm16 = (samples: Float32Array): Uint8Array => {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);
  samples.forEach((value, index) => {
    const sample = Math.max(-1, Math.min(1, value));
    view.setInt16(
      index * 2,
      Math.round(sample * (sample < 0 ? 32768 : 32767)),
      true
    );
  });
  return bytes;
};
// Canonical PCM WAV: both native bridges consume the captured audio.
const encodeWav = (pcm: Uint8Array): Uint8Array => {
  const wav = new Uint8Array(44 + pcm.length);
  const view = new DataView(wav.buffer);
  const label = (offset: number, value: string) => {
    [...value].forEach((char, i) =>
      view.setUint8(offset + i, char.charCodeAt(0))
    );
  };
  label(0, "RIFF");
  view.setUint32(4, 36 + pcm.length, true);
  label(8, "WAVE");
  label(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 16000, true);
  view.setUint32(28, 32000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  label(36, "data");
  view.setUint32(40, pcm.length, true);
  wav.set(pcm, 44);
  return wav;
};
const toPcm16 = async (blob: Blob): Promise<Uint8Array> => {
  const context = new AudioContext();
  try {
    const audio = await context.decodeAudioData(await blob.arrayBuffer());
    // Bound resampling memory use on mobile. Dictation has no retained recording.
    if (!audio.length || audio.duration > 600)
      throw new Error(
        "Recording must be shorter than 10 minutes for mobile transcription"
      );
    const offline = new OfflineAudioContext(
      1,
      Math.ceil(audio.duration * 16000),
      16000
    );
    const source = offline.createBufferSource();
    source.buffer = audio;
    source.connect(offline.destination);
    source.start();
    const resampled = await offline.startRendering();
    return encodePcm16(resampled.getChannelData(0));
  } finally {
    await context.close();
  }
};
const toBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
};

// Serialize conversion and native inference: never load several language models at once.
let pending: Promise<unknown> = Promise.resolve();
const transcribeSnagVocal = (
  blob: Blob,
  language: string,
  existingRun?: SpeechRun
): Promise<string> => {
  const run = existingRun ?? createSpeechRun(language);
  const task = pending
    .catch(() => undefined)
    .then(async () => {
      await run.measure("model-check", async () => {
        if ((await getSpeechModelStatus(language)) !== "ready")
          throw Object.assign(new Error("Download the speech model first"), {
            code: "SPEECH_MODEL_NOT_READY",
          });
      });
      const { Directory, Filesystem } = await run.measure(
        "filesystem",
        () => import("@capacitor/filesystem")
      );
      const path = `snag-transcription-${uuidv4()}.wav`;
      let fileAttempted = false;
      try {
        const pcm = await run.measure("audio-conversion", () => toPcm16(blob));
        run.event("audio-prepared", {
          audioBytes: blob.size,
          durationMs: pcm.length / 32,
        });
        fileAttempted = true;
        const { uri } = await run.measure("cache-write", () =>
          Filesystem.writeFile({
            data: toBase64(encodeWav(pcm)),
            directory: Directory.Cache,
            path,
          })
        );
        const text = await run.measure(
          "native-recognition",
          async () => {
            const result = await SnagSpeech.transcribeFile({ language, uri });
            if (typeof result.text !== "string" || result.text.length > 100_000)
              throw Object.assign(
                new Error("Invalid speech recognition result"),
                { code: "SPEECH_INVALID_RESULT" }
              );
            return result.text.trim();
          },
          pcm.length / 32 + 90000
        );
        if (!existingRun) run.finish(text ? "success" : "empty");
        return text;
      } finally {
        if (fileAttempted) {
          await Filesystem.deleteFile({
            directory: Directory.Cache,
            path,
          }).catch((cause: unknown) => {
            run.event(
              "cleanup-failed",
              { ...speechFailureDetails(cause), stage: "cache-delete" },
              true
            );
          });
        }
      }
    });
  pending = task;
  return task.catch((cause: unknown) => {
    if (!existingRun) run.finish("failed");
    throw cause;
  });
};

export {
  canTranscribeSnagVocals,
  downloadSpeechModel,
  encodePcm16,
  encodeWav,
  getSpeechModelStatus,
  snagDictationLanguage,
  transcribeSnagVocal,
};

export type { SpeechModelStatus };
